/**
 * Светофор руководителя — главный экран главврача (ТЗ §8, ui-blocks.md §5–§8).
 *
 * Все данные приходят одним запросом `GET /api/dashboard` (`useDashboard`).
 * Период живёт в query-строке (`?days=7` либо `?from=&to=`) — общий
 * `usePeriodState()`, панель периода стоит там же, где в макете при
 * `chief + today`: в `#growthSlot`, прямо над блоком отделений, то есть
 * выше всех блоков «за период».
 *
 * Порядок блоков — макетный: светофор → решения → новые за сутки → календарь →
 * контроль сроков → ближайшие сроки → нагрузка → «Мои дела» → отделения,
 * затем аналитика (категории и нормы, сравнительные тренды).
 *
 * «Мои дела» главврача — это, по ТЗ §8.2, все дела учреждения с семью чипами и
 * поиском. Блок собран на том же `useCaseList` + `GET /api/cases`, что и экран
 * `/cases`: поиск там серверный и идёт по номеру, теме, категории, исполнителю и
 * заявителю, чего клиентский поиск блока макета не умеет.
 */

import { useCallback, useMemo, useState } from 'react';

import { useDashboard } from '@/api/hooks';
import { ErrorState, LoadingState, pendingFailure } from '@/components/ErrorState';
import { useOpenCase } from '@/components/Modal';
import { PeriodBar, usePeriodState } from '@/components/PeriodBar';
import { useToast } from '@/components/Toast';

import { CASES_CHIPS, CaseFilters } from '@/components/cases/CaseFilters';
import { useCaseListParams } from '@/components/cases/useCaseListParams';
import { CasesTable, useCaseList } from '@/components/cases/CasesTable';
import { Pagination } from '@/components/cases/Pagination';

import { CaseListModal } from '@/components/dashboard/CaseListModal';
import { CategoryBlock } from '@/components/dashboard/CategoryBlock';
import { DeadlineCalendar } from '@/components/dashboard/DeadlineCalendar';
import { DeadlineControl } from '@/components/dashboard/DeadlineControl';
import { DecisionBlock } from '@/components/dashboard/DecisionBlock';
import { DepartmentGrowth } from '@/components/dashboard/DepartmentGrowth';
import { ExecutorLoad } from '@/components/dashboard/ExecutorLoad';
import { HotList } from '@/components/dashboard/HotList';
import { KpiGrid } from '@/components/dashboard/KpiGrid';
import { LegalBasisBlock } from '@/components/dashboard/LegalBasisBlock';
import { NewToday } from '@/components/dashboard/NewToday';
import { TodayStrip } from '@/components/dashboard/TodayStrip';
import { TrendsBlock } from '@/components/dashboard/TrendsBlock';
import { fmtDate } from '@/components/dashboard/format';
import { fmtTime } from '@/lib/format';
import type { ListRequest } from '@/components/dashboard/types';

export function DashboardChief() {
    const [period, setPeriod] = usePeriodState();
    const toast = useToast();
    const openCase = useOpenCase();
    const query = useDashboard(period);
    const [listRequest, setListRequest] = useState<ListRequest | null>(null);

    // «Мои дела»: фильтры, поиск и страница — в адресе страницы (обновление, «назад» и ссылка сохраняют выборку).
    const { filters, page, limit, changeFilters, changeParam } = useCaseListParams(CASES_CHIPS);

    const casesQuery = useMemo(
        () => ({ ...filters, page, limit, sort: 'deadline' as const }),
        [filters, page, limit],
    );
    const cases = useCaseList(casesQuery);

    const openList = useCallback((request: ListRequest) => {
        setListRequest(request);
    }, []);

    const closeList = useCallback(() => {
        setListRequest(null);
    }, []);

    const data = query.data;

    const periodBar = (
        <PeriodBar value={period} onChange={setPeriod} range={data ? data.range : null} />
    );

    if (!data) {
        // Ошибка — сразу после первой неудачи; повторы идут в фоне, при успехе экран заполняется сам.
        const failure = pendingFailure(query);
        return (
            <>
                {periodBar}
                {failure ? (
                    <ErrorState error={failure} onRetry={() => void query.refetch()}>
                        {query.isFetching ? <span className="retry-note">Повторяем запрос автоматически…</span> : null}
                    </ErrorState>
                ) : (
                    <LoadingState label="Загружаем данные панели…" />
                )}
            </>
        );
    }

    const isChief = data.role === 'chief';

    return (
        <>
            <div className="page-header">
                <h1>
                    🚦 Светофор руководителя
                    <span>данные на {fmtDate(data.generated_at)}</span>
                </h1>
                <span className="update-info">🔄 обновлено: {fmtTime(data.generated_at)}</span>
            </div>

            <section id="secChiefToday">
                {data.today_strip && <TodayStrip strip={data.today_strip} />}

                <KpiGrid tiles={data.kpi} openList={openList} toast={toast} />

                <DecisionBlock
                    decisions={data.decisions}
                    executors={data.load ? data.load.rows : []}
                    openCase={openCase}
                    toast={toast}
                />

                <NewToday newToday={data.new_today} openCase={openCase} />

                <DeadlineCalendar calendar={data.calendar} openList={openList} toast={toast} />

                <DeadlineControl deadlines={data.deadlines} />

                <HotList hot={data.hot} openCase={openCase} />

                {data.load && (
                    <ExecutorLoad load={data.load} todayIso={data.range.today} openList={openList} />
                )}

                <CaseFilters value={filters} onChange={changeFilters} chips={CASES_CHIPS} />

                <div className="stat-block" style={{ marginBottom: 18 }}>
                    <div className="block-header">
                        <span>
                            📁 Мои дела{' '}
                            <span style={{ textTransform: 'none', fontWeight: 500, color: '#56697f' }}>
                                · все дела учреждения
                            </span>
                        </span>
                    </div>
                    <div className="block-body" id="myBlock" aria-busy={cases.isFetching}>
                        <CasesTable
                            items={cases.items}
                            variant="my"
                            onOpen={openCase}
                            loading={cases.isPending}
                            error={cases.error}
                        />
                        {cases.truncated ? (
                            <div className="case-more">
                                Выборка «Повторные» приходит с сервера не более чем 200 делами: всего
                                таких дел {cases.serverTotal}. Уточните поиск, чтобы увидеть остальные.
                            </div>
                        ) : null}
                    </div>
                    <Pagination
                        page={cases.page}
                        limit={cases.limit}
                        total={cases.total}
                        pages={cases.pages}
                        onPage={(next) => changeParam('page', next)}
                        onLimit={(next) => changeParam('limit', next)}
                    />
                </div>

                <div id="growthSlot">
                    {periodBar}
                    <DepartmentGrowth
                        growth={data.growth}
                        range={data.range}
                        period={period}
                        openCase={openCase}
                    />
                </div>
            </section>

            <section id="secAnalytics">
                <div className="two-col">
                    <div className="col col-left">
                        <CategoryBlock
                            categoriesOpen={data.categories_open}
                            categoriesPeriod={data.categories_period}
                            range={data.range}
                            period={period}
                        />
                    </div>
                    {data.legal && (
                        <div className="col col-right">
                            <LegalBasisBlock legal={data.legal} openList={openList} />
                        </div>
                    )}
                </div>

                <TrendsBlock
                    trends={data.trends}
                    range={data.range}
                    period={period}
                    canExport={isChief}
                    toast={toast}
                />
            </section>

            <p className="footer-note">
                Блоки со значком <span className="scope-tag">на сейчас</span> — снимок открытых дел
                на {fmtDate(data.range.today)} и не зависят от периода; блоки со значком{' '}
                <span className="scope-tag period">за период</span> пересчитываются под выбранный
                интервал.
            </p>

            {listRequest && (
                <CaseListModal
                    title={listRequest.title}
                    kpi={listRequest.kpi}
                    params={listRequest.params}
                    period={period}
                    onClose={closeList}
                />
            )}
        </>
    );
}

export default DashboardChief;
